/**
 * 보안 파일 편집 모달
 * 파일 메타데이터 수정 (카테고리, 설명, 태그, PIN)
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../UI';
import { 
  Edit, 
  X, 
  Shield, 
  Tag,
  Folder,
  Info,
  AlertCircle
} from 'lucide-react';
import SecureFilePinModal from './SecureFilePinModal';
import './SecureFileUpload.css';

function SecureFileEditModal({ file, isOpen, onClose, onUpdate }) {
  const [metadata, setMetadata] = useState({
    category: '',
    description: '',
    tags: [],
    enablePin: false,
    removePin: false
  });
  const [pinForRemoval, setPinForRemoval] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 파일 정보로 초기화
  useEffect(() => {
    if (file) {
      setMetadata({
        category: file.category || 'general',
        description: file.description || '',
        tags: file.tags || [],
        enablePin: file.isPinProtected || false,
        removePin: false
      });
      setPinForRemoval('');
      setShowPinModal(false);
      setError('');
    }
  }, [file]);

  // 태그 추가
  const handleAddTag = () => {
    if (tagInput.trim() && !metadata.tags.includes(tagInput.trim())) {
      setMetadata(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  // 태그 제거
  const handleRemoveTag = (tag) => {
    setMetadata(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // PIN 토글 (보호 추가/제거)
  const handlePinToggle = () => {
    if (file.isPinProtected && metadata.enablePin) {
      // PIN 보호 제거 시도 - PIN 입력 요구
      setShowPinModal(true);
    } else {
      // PIN 보호 추가 또는 토글
      setMetadata(prev => ({
        ...prev,
        enablePin: !prev.enablePin,
        removePin: false
      }));
    }
  };

  // 업데이트 처리
  const handleUpdate = async () => {
    setError('');

    // Master PIN 설정 확인 (PIN 보호를 활성화하려는 경우)
    if (metadata.enablePin && !file.isPinProtected) {
      // Master PIN이 설정되어 있는지 확인은 SecureFilesList에서 처리
      // 여기서는 단순히 isPinProtected 플래그만 업데이트
    }

    setLoading(true);

    try {
      // 업데이트할 데이터 준비
      const updates = {
        category: metadata.category,
        description: metadata.description,
        tags: metadata.tags
      };

      // PIN 보호 상태 처리
      if (metadata.enablePin !== file.isPinProtected) {
        updates.isPinProtected = metadata.enablePin; // PIN 보호 상태 변경
      }

      // PIN 보호 제거 시 PIN 전달
      let updatePin = null;
      if (!metadata.enablePin && file.isPinProtected) {
        if (!pinForRemoval) {
          // PIN이 없으면 모달 표시
          setShowPinModal(true);
          setLoading(false);
          return;
        }
        updatePin = pinForRemoval;
      }
      
      await onUpdate(file.id, updates, updatePin);
      onClose();
    } catch (err) {
      setError(err.message || '파일 정보 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 카테고리 목록
  const categories = [
    { value: 'general', label: '일반' },
    { value: 'document', label: '문서' },
    { value: 'image', label: '이미지' },
    { value: 'finance', label: '금융' },
    { value: 'personal', label: '개인' },
    { value: 'work', label: '업무' },
    { value: 'other', label: '기타' }
  ];

  if (!file) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="파일 정보 수정" size="md">
      <div className="secure-file-upload">
        {/* 파일 정보 표시 */}
        <div className="upload-metadata">
          <div className="form-group">
            <label>파일명</label>
            <input
              type="text"
              value={file.originalName}
              disabled
              className="form-input"
              style={{ opacity: 0.7 }}
            />
          </div>

          {/* 카테고리 선택 */}
          <div className="form-group">
            <label>
              <Folder size={14} />
              카테고리
            </label>
            <select
              value={metadata.category}
              onChange={(e) => setMetadata(prev => ({ ...prev, category: e.target.value }))}
              className="form-select"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* 설명 입력 */}
          <div className="form-group">
            <label>
              <Info size={14} />
              설명
            </label>
            <textarea
              placeholder="파일에 대한 설명을 입력하세요..."
              value={metadata.description}
              onChange={(e) => setMetadata(prev => ({ ...prev, description: e.target.value }))}
              className="form-textarea"
              rows="3"
            />
          </div>

          {/* 태그 입력 */}
          <div className="form-group">
            <label>
              <Tag size={14} />
              태그
            </label>
            <div className="tag-input-wrapper">
              <input
                type="text"
                placeholder="태그 입력 후 Enter"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                className="form-input"
              />
              <button 
                type="button"
                onClick={handleAddTag}
                className="tag-add-btn"
                disabled={!tagInput.trim()}
              >
                추가
              </button>
            </div>
            {metadata.tags.length > 0 && (
              <div className="tags-list">
                {metadata.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* PIN 보호 설정 */}
          <div className="form-group">
            <div className="pin-toggle-wrapper">
              <button
                type="button"
                className={`pin-toggle-btn ${metadata.enablePin ? 'active' : ''}`}
                onClick={handlePinToggle}
              >
                <Shield size={16} />
                {metadata.enablePin ? 'PIN 보호중' : 'PIN 보호하기'}
              </button>
              <span className="pin-hint">
                {metadata.enablePin
                  ? 'Master PIN으로 보호됩니다'
                  : '클릭하여 PIN 보호 설정'}
              </span>
            </div>
            {metadata.enablePin && (
              <p className="pin-info">
                <Info size={12} />
                이 파일은 Master PIN으로 보호됩니다. 다운로드, 미리보기, 삭제 시 PIN 입력이 필요합니다.
              </p>
            )}
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="upload-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* 액션 버튼 */}
        <div className="upload-actions">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            취소
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpdate}
            disabled={loading}
            loading={loading}
          >
            <Edit size={16} />
            수정 완료
          </Button>
        </div>
      </div>
      
      {/* PIN 입력 모달 (PIN 보호 제거 시) */}
      {showPinModal && (
        <SecureFilePinModal
          isOpen={showPinModal}
          onClose={() => {
            setShowPinModal(false);
            setPinForRemoval('');
          }}
          onSubmit={async (pin) => {
            setPinForRemoval(pin);
            setMetadata(prev => ({
              ...prev,
              enablePin: false,
              removePin: true
            }));
            setShowPinModal(false);
            
            // PIN 입력 후 바로 업데이트 실행
            try {
              const updates = {
                category: metadata.category,
                description: metadata.description,
                tags: metadata.tags,
                isPinProtected: false
              };
              
              await onUpdate(file.id, updates, pin);
              onClose();
            } catch (err) {
              setError(err.message || '파일 정보 수정에 실패했습니다.');
            }
          }}
          mode="verify"
          title="PIN을 입력하여 보호 해제"
        />
      )}
    </Modal>
  );
}

export default SecureFileEditModal;